import React from 'react';
import { useTranslation } from 'react-i18next';

import PageLayout from '@/components/layout/PageLayout';
import { IndicatorCard } from '@/components/ui/cards/IndicatorCard';
import { ExportDropdown } from '@/components/ui/filters/ExportDropdown';
import { FilterDropdown } from '@/components/ui/filters/FilterDropdown';
import Table from '@/components/ui/tables/Table';
import { useDeviceSummary } from '@/hooks/device/useDeviceSummary';
import { useDeviceList } from '@/hooks/device/useDeviceList';
import { useDeviceExport } from '@/hooks/device/useDeviceExport';


export const DeviceList: React.FC = () => {
  const { t: translate } = useTranslation('device');
  const { totals, isLoading } = useDeviceSummary();
  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    filters,
    setFilters,
    filterConfig,
    columns,
    actions,
    onSearch,
    onSortChange,
    loading,
    debouncedSearchText,
  } = useDeviceList();
  const { onExport, isExporting } = useDeviceExport({
    searchText: debouncedSearchText,
    typeFilter: filters.typeFilter,
    statusFilter: filters.statusFilter,
  });

  return (
    <PageLayout isLoading={isLoading}>
      <div className="flex flex-col gap-6" data-testid="device-list-page">
        {/* Indicator Cards */}
        <div className="flex flex-row gap-4 overflow-x-auto pb-4 px-2">
          <IndicatorCard
            label={translate('indicators.total')}
            value={totals.total}
            variant="default"
            data-testid="indicator-total"
          />
          <IndicatorCard
            label={translate('indicators.conectados')}
            value={totals.connected}
            variant="default"
            data-testid="indicator-conectados"
          />
          <IndicatorCard
            label={translate('indicators.desconectados')}
            value={totals.disconnected}
            variant="default"
            data-testid="indicator-desconectados"
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <FilterDropdown
            filters={filterConfig}
            values={{ typeFilter: filters.typeFilter, statusFilter: filters.statusFilter }}
            onChangeFilter={(key, value) => setFilters((previous) => ({
              ...previous,
              [key]: Array.isArray(value) ? value[0] : value,
            }))}
          />
          <ExportDropdown
            onExport={onExport}
            placement="right"
            availableFormats={['EXCEL']}
            isLoading={isExporting}
            disabled={total === 0}
          />
        </div>

        {/* Devices Table */}
        <Table
          columns={columns}
          data={rows}
          actions={actions}
          totalRows={total}
          searchable
          searchPlaceholder={translate('list.searchPlaceholder')}
          onSearch={onSearch}
          serverSideSort
          onSortChange={onSortChange}
          initialSort={{ key: 'name', direction: 'asc' }}
          pagination={{
            page,
            pageSize,
            total,
            onChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          data-testid="device-table"
          isLoading={loading}
        />
      </div>
    </PageLayout>
  );
};

export default DeviceList;
